import { useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BrainNoteDTO } from "@app-factory/shared";
import { api } from "../../lib/api";
import { cs } from "../../i18n/cs";
import { Btn, Chip, EmptyState, FilterPill, PageHeader, Panel } from "@/design/primitives";
import { relativeTime } from "../../lib/format";
import { BrainGraph } from "./BrainGraph";

/**
 * Factory brain — poučení z provozu.
 *
 * Poznámky vznikají automaticky, ale jako KANDIDÁTI: bez spouštěče (`kdy`)
 * se workerovi nikdy nenabídnou. Tahle obrazovka je místo, kde se z kandidáta
 * stane poučení — a zároveň jediné místo, kde je na první pohled vidět, jestli
 * brain neshnil.
 */

type ScopeFilter = "ALL" | "FACTORY" | "PROJECT";

export function BrainPage() {
  const navigate = useNavigate();
  // rozsah i projekt drží adresa — odkaz na „poučení projektu" jde sdílet
  const search = useSearch({ from: "/shell/brain" }) as { scope?: ScopeFilter; project?: string };
  const scope: ScopeFilter = search.scope ?? "ALL";
  const setScope = (next: ScopeFilter) =>
    void navigate({
      to: "/brain",
      search: { ...(next === "ALL" ? {} : { scope: next }), ...(projectId ? { project: projectId } : {}) },
    });
  // Bez filtru projektu byla volba „jen projekt" míchanice poučení ze všech
  // projektů dohromady — tedy přesně to, co má scope PROJECT rozlišovat.
  const projectId = search.project ?? "";
  const setProjectId = (next: string) =>
    void navigate({
      to: "/brain",
      search: { ...(scope === "ALL" ? {} : { scope }), ...(next ? { project: next } : {}) },
    });
  const [selected, setSelected] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const projects = useQuery({ queryKey: ["projects"], queryFn: () => api.projects() });
  const filter = {
    ...(scope === "ALL" ? {} : { scope }),
    ...(projectId ? { projectId } : {}),
  };

  const notesQuery = useQuery({
    queryKey: ["brain", "notes", scope, projectId],
    queryFn: () => api.brainNotes(filter),
  });
  const graphQuery = useQuery({
    queryKey: ["brain", "graph", scope, projectId],
    queryFn: () => api.brainGraph(filter),
  });

  const notes = notesQuery.data?.notes ?? [];
  const note = notes.find((n) => n.slug === selected) ?? null;

  const stats = useMemo(() => {
    const total = notes.length;
    const draft = notes.filter((n) => !n.indexed).length;
    const recurring = notes.filter((n) => n.occurrences > 1).length;
    return { total, draft, recurring };
  }, [notes]);

  return (
    <div>
      <PageHeader
        title="Brain"
        kicker="POUČENÍ"
        lead={cs.brain.subtitle}
        right={
          <>
            {(["ALL", "FACTORY", "PROJECT"] as ScopeFilter[]).map((s) => (
              <FilterPill
                key={s}
                active={scope === s}
                onClick={() => {
                  setScope(s);
                  setSelected(null);
                }}
                testId={`brain-scope-${s}`}
              >
                {cs.brain.scopes[s]}
              </FilterPill>
            ))}
            <select
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                setSelected(null);
              }}
              className="af-mono cursor-pointer px-3 py-[7px] text-[12px]"
              style={{
                borderRadius: "var(--radius-pill)",
                border: "1px solid var(--border-default)",
                background: "var(--surface-inset)",
                color: "var(--text-secondary)",
              }}
              data-testid="brain-project"
            >
              <option value="">{cs.brain.allProjects}</option>
              {(projects.data?.projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </>
        }
      />

      <div className="mb-3 af-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.brain.stats(stats.total, stats.draft, stats.recurring)}
      </div>

      <div className="grid gap-3 lg:grid-cols-[1.35fr_1fr]">
        <Panel className="flex flex-col overflow-hidden p-0" style={{ height: "calc(100vh - 230px)", minHeight: 460 }}>
          <Legend />
          <div className="min-h-0 flex-1">
            {graphQuery.data && graphQuery.data.nodes.length > 0 ? (
              <BrainGraph graph={graphQuery.data} selected={selected} onSelect={setSelected} />
            ) : (
              <div className="flex h-full items-center justify-center p-8">
                <EmptyState title={cs.brain.emptyGraph} note={cs.brain.emptyGraphNote} />
              </div>
            )}
          </div>
        </Panel>

        <div className="flex min-h-0 flex-col gap-3 overflow-auto" style={{ maxHeight: "calc(100vh - 230px)" }}>
          {note ? (
            <NoteEditor
              note={note}
              onBack={() => setSelected(null)}
              onDone={() => {
                void queryClient.invalidateQueries({ queryKey: ["brain"] });
              }}
            />
          ) : (
            <NoteList notes={notes} onSelect={setSelected} />
          )}
        </div>
      </div>
    </div>
  );
}

/** Bez legendy je graf jen hezký obrázek — tvary v něm nesou informaci. */
function Legend() {
  const items: { color: string; dashed?: boolean; label: string }[] = [
    { color: "var(--positive)", label: cs.brain.legendFactory },
    { color: "var(--accent)", label: cs.brain.legendProject },
    { color: "var(--info)", label: cs.brain.legendMap },
    { color: "var(--warning)", dashed: true, label: cs.brain.legendDraft },
  ];
  return (
    <div
      className="flex flex-wrap gap-x-3 gap-y-1 px-3 py-2 text-[11px]"
      style={{ color: "var(--text-tertiary)", borderBottom: "1px solid var(--border-default)" }}
    >
      {items.map((i) => (
        <span key={i.label} className="flex items-center gap-1">
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 99,
              background: i.dashed ? "transparent" : i.color,
              border: i.dashed ? `1px dashed ${i.color}` : "none",
              display: "inline-block",
            }}
          />
          {i.label}
        </span>
      ))}
      <span>{cs.brain.legendSize}</span>
    </div>
  );
}

function NoteList({
  notes,
  onSelect,
}: {
  notes: BrainNoteDTO[];
  onSelect: (slug: string) => void;
}) {
  if (notes.length === 0) {
    return (
      <Panel className="p-4">
        <EmptyState title={cs.brain.emptyList} note={cs.brain.emptyListNote} />
      </Panel>
    );
  }
  return (
    <Panel className="p-3">
      <div className="mb-2 text-[12px] tracking-[0.12em]" style={{ color: "var(--text-tertiary)" }}>
        {cs.brain.listTitle}
      </div>
      <div className="flex flex-col gap-1.5">
        {notes.map((n) => (
          <button
            key={n.slug}
            onClick={() => onSelect(n.slug)}
            className="w-full px-2 py-1.5 text-left"
            style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--border-default)" }}
            data-testid={`brain-note-${n.slug}`}
          >
            <div className="flex items-baseline gap-2">
              <span className="text-[11px]" style={{ color: "var(--text-primary)" }}>
                {n.title}
              </span>
              {n.occurrences > 1 ? (
                <span className="text-[11px]" style={{ color: "var(--warning)" }}>
                  {cs.brain.occurrences(n.occurrences)}
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {n.indexed ? n.readWhen : cs.brain.noTrigger}
              {" · "}
              {relativeTime(n.lastSeenAt)}
            </div>
          </button>
        ))}
      </div>
    </Panel>
  );
}

/**
 * Editor jedné poznámky. Podstatné pole je `kdy`: bez něj se poučení
 * workerovi nikdy nenabídne, takže je tu první a s vysvětlením.
 */
function NoteEditor({
  note,
  onBack,
  onDone,
}: {
  note: BrainNoteDTO;
  onBack: () => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(note.title);
  const [readWhen, setReadWhen] = useState(note.readWhen);
  const [body, setBody] = useState(note.body);

  const save = useMutation({
    mutationFn: (input: Parameters<typeof api.updateBrainNote>[1]) =>
      api.updateBrainNote(note.slug, input),
    onSuccess: onDone,
  });

  const dirty = title !== note.title || readWhen !== note.readWhen || body !== note.body;

  return (
    <Panel className="p-3" data-testid="brain-editor">
      <div className="mb-2 flex items-center gap-2">
        <button
          onClick={onBack}
          className="text-[12px]"
          style={{ color: "var(--text-tertiary)" }}
          data-testid="brain-back"
        >
          {cs.brain.back}
        </button>
        <Chip tone={note.scope === "FACTORY" ? "green" : "cyan"}>
          {note.scope === "FACTORY" ? cs.brain.scopes.FACTORY : cs.brain.scopes.PROJECT}
        </Chip>
        {note.occurrences > 1 ? (
          <Chip tone="amber">{cs.brain.occurrences(note.occurrences)}</Chip>
        ) : null}
        {note.needsReview ? <Chip tone="amber">{cs.brain.needsReview}</Chip> : null}
        <span className="ml-auto font-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {note.slug}
        </span>
      </div>

      <label className="mb-2 block">
        <span className="text-[11px] tracking-[0.1em]" style={{ color: "var(--text-tertiary)" }}>
          {cs.brain.fieldTitle}
        </span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-0.5 w-full bg-transparent px-2 py-1 text-[12px]"
          style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--border-default)", color: "var(--text-primary)" }}
          data-testid="brain-title"
        />
      </label>

      <label className="mb-2 block">
        <span className="text-[11px] tracking-[0.1em]" style={{ color: "var(--warning)" }}>
          {cs.brain.fieldWhen}
        </span>
        <input
          value={readWhen}
          onChange={(e) => setReadWhen(e.target.value)}
          placeholder={cs.brain.fieldWhenPlaceholder}
          className="mt-0.5 w-full bg-transparent px-2 py-1 text-[12px]"
          style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--border-default)", color: "var(--text-primary)" }}
          data-testid="brain-readwhen"
        />
        <span className="mt-0.5 block text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.brain.fieldWhenHelp}
        </span>
      </label>

      <label className="mb-2 block">
        <span className="text-[11px] tracking-[0.1em]" style={{ color: "var(--text-tertiary)" }}>
          {cs.brain.fieldBody}
        </span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={9}
          className="mt-0.5 w-full bg-transparent px-2 py-1 font-mono text-[11px]"
          style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--border-default)", color: "var(--text-secondary)" }}
          data-testid="brain-body"
        />
      </label>

      <div className="flex flex-wrap gap-2">
        <Btn
          variant="primary"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate({ title, readWhen, body, reviewed: true })}
          data-testid="brain-save"
        >
          {cs.brain.save}
        </Btn>
        {note.scope !== "FACTORY" ? (
          <Btn
            onClick={() => save.mutate({ scope: "FACTORY", reviewed: true })}
            data-testid="brain-promote"
          >
            {cs.brain.promote}
          </Btn>
        ) : null}
        <Btn
          variant="danger"
          onClick={() => save.mutate({ archived: true })}
          data-testid="brain-archive"
        >
          {cs.brain.archive}
        </Btn>
      </div>
      {note.scope !== "FACTORY" ? (
        <p className="mt-1.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.brain.promoteHelp}
        </p>
      ) : null}
    </Panel>
  );
}
